import { useState, useEffect, useRef, useCallback } from "react";

export function useUrlSortingSync<T extends Record<string, unknown>>(
  urlSorting: T,
  updateSearchParams: (updates: Partial<T>) => void,
  initialLocalSorting?: T
) {
  const [localSorting, setLocalSorting] = useState<T>(
    initialLocalSorting ?? urlSorting
  );

  const prevUrlSortingRef = useRef<T>(urlSorting);
  const prevLocalSortingRef = useRef<T>(localSorting);
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    const urlSortingStr = JSON.stringify(urlSorting);
    const prevUrlSortingStr = JSON.stringify(prevUrlSortingRef.current);

    if (urlSortingStr !== prevUrlSortingStr) {
      isInternalUpdateRef.current = true;
      setLocalSorting(urlSorting);
      prevUrlSortingRef.current = urlSorting;
      prevLocalSortingRef.current = urlSorting;
    }
  }, [urlSorting]);

  useEffect(() => {
    const localSortingStr = JSON.stringify(localSorting);
    const prevLocalSortingStr = JSON.stringify(prevLocalSortingRef.current);
    const urlSortingStr = JSON.stringify(urlSorting);

    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (
      localSortingStr !== prevLocalSortingStr &&
      localSortingStr !== urlSortingStr
    ) {
      prevLocalSortingRef.current = localSorting;
      updateSearchParams(localSorting);
    }
  }, [localSorting, urlSorting, updateSearchParams]);

  const updateLocalSorting = useCallback(
    (updates: Partial<T> | ((prev: T) => T)) => {
      setLocalSorting((prev) => {
        if (typeof updates === "function") {
          return updates(prev);
        }
        return { ...prev, ...updates };
      });
    },
    []
  );

  return {
    localSorting,
    setLocalSorting: updateLocalSorting,
  };
}
